import type { PublicProfilePayload } from './types';
import { PUBLIC_JOURNEY_PREVIEW_LIMIT } from './visibility-defaults';

function journeyVisible(data: PublicProfilePayload): boolean {
  return (
    data.visibility.show_journey_preview ||
    data.visibility.journey_map_visibility === 'preview' ||
    data.visibility.journey_map_visibility === 'full'
  );
}

/**
 * Project the owner's already-public payload into the same shape a visitor may
 * render. This keeps "View as public" useful without introducing a second
 * visibility model or exposing owner-only metadata client-side in the preview.
 */
export function projectPublicPreviewPayload(
  data: PublicProfilePayload
): PublicProfilePayload | null {
  if (!data.visibility.profile_enabled) return null;

  const stories = data.visibility.show_stories ? data.stories : [];
  const hasFullPublicJourney = data.visibility.journey_map_visibility === 'full';
  const records = data.visibility.show_records
    ? data.records.map((record) =>
        journeyVisible(data) && hasFullPublicJourney ? record : { ...record, location: null }
      )
    : [];
  const proofCards = data.visibility.show_proof_cards ? data.proofCards : [];
  const journeyMoments = journeyVisible(data)
    ? hasFullPublicJourney
      ? data.journeyMoments
      : data.journeyMoments.slice(0, PUBLIC_JOURNEY_PREVIEW_LIMIT)
    : [];
  const familyItems = data.visibility.show_family_preview ? data.familyItems : [];
  const circles = data.visibility.show_public_circles
    ? data.circles.filter(
        (circle) =>
          circle.privacy === 'Public Proof' ||
          (circle.privacy === 'Circle-only' && circle.is_discoverable)
      )
    : [];
  const soundtrack = data.soundtrackState === 'public' ? data.soundtrack || [] : [];

  return {
    ...data,
    profile: {
      ...data.profile,
      vault_id: data.visibility.show_erv_id ? data.profile.vault_id : '',
      erv_id: data.visibility.show_erv_id ? data.profile.erv_id : null,
      location: data.visibility.show_location ? data.profile.location : null,
      created_at: data.visibility.show_member_since ? data.profile.created_at : '',
    },
    visibility: {
      ...data.visibility,
      featured_story_id: null,
      featured_record_id: null,
    },
    stats: {
      ...data.stats,
      publicStories: data.visibility.show_stories ? data.stats.publicStories : 0,
      publicPulsePosts: data.visibility.show_stories ? data.stats.publicPulsePosts : 0,
      publicRecords: data.visibility.show_records ? data.stats.publicRecords : 0,
      verifiedRecords: data.visibility.show_records ? data.stats.verifiedRecords : 0,
      publicProofCards: data.visibility.show_proof_cards ? data.stats.publicProofCards : 0,
      publicJourneyMoments: journeyVisible(data) ? data.stats.publicJourneyMoments : 0,
      publicCircles: circles.length,
      followers: data.visibility.show_followers ? data.stats.followers : 0,
      following: data.visibility.show_following ? data.stats.following : 0,
    },
    stories,
    pulseHighlights: data.visibility.show_stories ? data.pulseHighlights : [],
    records,
    proofCards,
    journeyMoments,
    familyItems,
    familySummary: data.visibility.show_family_preview
      ? data.familySummary
      : {
          public_family_members_count: 0,
          public_family_records_count: 0,
          public_heritage_items_count: 0,
          public_generations_count: null,
          preview_items: [],
        },
    circles,
    featuredStory:
      data.visibility.show_stories &&
      data.featuredStory &&
      stories.some((story) => story.id === data.featuredStory?.id)
        ? data.featuredStory
        : null,
    featuredRecord:
      data.visibility.show_records &&
      data.featuredRecord &&
      records.some((record) => record.id === data.featuredRecord?.id)
        ? data.featuredRecord
        : null,
    isOwner: false,
    viewerId: null,
    soundtrack,
    soundtrackState: soundtrack.length > 0 ? 'public' : 'empty',
  };
}
